import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDeleteModal } from "../../../../hooks/shared/useDeleteModal";
import { filterRowsByQuery } from "../../../../utils/shared/searchHelpers";
import { formatValidationErrors } from "../../utils/adminApiHelpers";
import {
  buildNewsFormData,
  buildNewsFormDataForUpdate,
  createAdminNews,
  deleteAdminNews,
  fetchAdminNewsById,
  fetchAdminNewsList,
  unwrapList,
  unwrapOne,
  updateAdminNews,
} from "../api";
import { getNewsColumns } from "../constants/newsColumns";
import { getNewsFields } from "../constants/newsFields";
import { EMPTY_NEWS_FORM } from "../constants/emptyDefaults";
import { normalizeNewsItem } from "../utils/normalizeNews";

export function useManageNews() {
  const { t } = useTranslation("dashboard");
  const tk = useCallback((key) => t(`manageNews.${key}`), [t]);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const deleteModal = useDeleteModal();
  const [editingItem, setEditingItem] = useState(null);
  const [newFormKey, setNewFormKey] = useState(0);
  const [submitError, setSubmitError] = useState(null);

  const editingId = editingItem?.id;

  const { data: editingDetail, isFetching: isEditDetailLoading } = useQuery({
    queryKey: ["admin", "news", "detail", editingId],
    queryFn: async () => {
      const res = await fetchAdminNewsById(editingId);
      const raw = unwrapOne(res);
      return normalizeNewsItem(raw) ?? normalizeNewsItem(raw?.data);
    },
    enabled: editingId != null,
  });

  const formDefaults = useMemo(() => {
    if (!editingItem) return { ...EMPTY_NEWS_FORM };
    const source = editingDetail ?? editingItem;
    return {
      title_ar: source.title_ar ?? "",
      title_en: source.title_en ?? "",
      content_ar: source.content_ar ?? "",
      content_en: source.content_en ?? "",
    };
  }, [editingItem, editingDetail]);

  const fields = useMemo(() => getNewsFields(tk), [tk]);
  const columns = useMemo(() => getNewsColumns(tk), [tk]);

  const {
    data: news = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "news"],
    queryFn: async () => {
      const res = await fetchAdminNewsList();
      return unwrapList(res).map(normalizeNewsItem).filter(Boolean);
    },
  });

  const createMutation = useMutation({
    mutationFn: (formData) => createAdminNews(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "news"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => updateAdminNews(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "news"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAdminNews(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "news"] });
    },
  });

  const handleSubmit = async (data) => {
    setSubmitError(null);
    const formData = editingItem
      ? buildNewsFormDataForUpdate(data)
      : buildNewsFormData(data);
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({
          id: editingItem.id,
          formData,
        });
        setEditingItem(null);
      } else {
        await createMutation.mutateAsync(formData);
      }
      setNewFormKey((k) => k + 1);
    } catch (err) {
      setSubmitError(formatValidationErrors(err));
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.deleteId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteModal.deleteId);
    } finally {
      deleteModal.close();
    }
  };

  const filteredNews = useMemo(
    () => filterRowsByQuery(news, search, ["title_en", "title_ar"]),
    [news, search]
  );

  const errorMessage =
    error?.response?.data?.message ||
    error?.message ||
    (isError ? tk("alerts.loadFailed") : null);

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    tk,
    search,
    setSearch,
    deleteModal,
    editingItem,
    setEditingItem,
    newFormKey,
    submitError,
    isEditDetailLoading,
    formDefaults,
    fields,
    columns,
    filteredNews,
    isLoading,
    errorMessage,
    pending,
    handleSubmit,
    confirmDelete,
    handleEdit: setEditingItem,
    handleDeleteClick: deleteModal.open,
    cancelEdit: () => setEditingItem(null),
  };
}
